import { FC, memo, ReactElement, ReactNode, useCallback, useMemo, useState } from 'react';
import Table from 'react-bootstrap/Table';
import PatientRow from './PatientRow';
import { PatientListDto, PatientTherapyBillingInfoDto } from '../../../../shared/dtos/patient.dto';
import i18next from 'i18next';
import { Button, Form, Popover } from 'react-bootstrap';
import SortableTableHeader from './SortableTableHeader';
import SearchBar from '../SearchBar';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import { Filter } from 'lucide-react';
import { OverlayChildren } from 'react-bootstrap/cjs/Overlay';
import {
  createPatientTableStore,
  FilterState,
  SortKey,
  SortState,
  usePatientTableSelector
} from './patientstable.store';
import { useExtendedAuth } from '../../util/auth0';
import { PatientTherapyStatus } from '../../../../shared/utils/enums';

export type PatientUpload = {
  patient: PatientListDto;
  isCompletion: boolean;
};

export type PatientBilling = {
  patientId: string;
  billingInfo?: PatientTherapyBillingInfoDto
}

export type ChangeTherapist = {
  patientId: string;
  currentTherapistId: string;
}

type PatientsTableProps = {
  page: 'default' | 'admin',
  rows: PatientListDto[];
  showAdditionalInfo: boolean;
  onNavigate?: (therapyId: string) => void;
  onPatientEdit?: (row: PatientListDto) => void;
  onUploadFile?: (data: PatientUpload) => void;
  onSetCompletionConversation: (patientId: string) => void;
  onCompletePatient: (patientId: string) => void;
  onBillingPatient?: (data: PatientBilling) => void;
  onDeletePatient?: (patientId: string) => void;
  onCancelPatient?: (patientId: string) => void;
  onExportFile?: (patientId: string) => void;
  onTherapistChange?: (data: ChangeTherapist) => void;
  toolbarRight?: ReactNode;
};

const normalize = (value: unknown): string => String(value ?? '').toLowerCase().trim();
const compareString = (a: string, b: string): number => a.localeCompare(b, undefined, { sensitivity: 'base' });
const compareNumber = (a: number, b: number): number => a - b;
const compareBoolean = (a: boolean, b: boolean): number => Number(a) - Number(b);
const compareDate = (a?: string, b?: string): number => {
  const aDate: number = a ? Date.parse(a) : NaN;
  const bDate: number = b ? Date.parse(b) : NaN;
  
  const aValue: boolean = Number.isFinite(aDate);
  const bValue: boolean = Number.isFinite(bDate);
  
  if (!aValue && !bValue) {
    return 0;
  }
  
  if (!aValue) {
    return 1;
  }
  
  if (!bValue) {
    return -1;
  }
  
  return aDate - bDate;
};

const getPatientLabel = (row: PatientListDto): string => row.patientInfo.fullName.trim();
const hasOnboarded = (row: PatientListDto): boolean => Boolean(row.patientInfo.onBoardedAt);
const getStatusKey = (row: PatientListDto): string => String(row.patientInfo.status);
const hasTrainingPlan = (row: PatientListDto): boolean => Boolean(row.trainingPlanEndDate);
const getTeleRehaProgramTitle = (row: PatientListDto): string => {
  if (!row.telerehabilitationProgram) {
    return '';
  } 
  
  return i18next.language === 'de' ? row.telerehabilitationProgram.germanName : row.telerehabilitationProgram.englishName;
};
const getTeleRehaProgramId = (row: PatientListDto): string => row.telerehabilitationProgram?.id ?? '';
const getDocumentScore = (row: PatientListDto): number => Number(Boolean(row.applicationDocumentUrl)) + Number(Boolean(row.completionDocumentUrl));
const getTherapyBillingsUnitAmount = (row: PatientListDto): number => Number(row.therapyBillingInfo?.unitAmount ?? 0);
const isBilled = (row: PatientListDto): boolean => Boolean(row.therapyBillingInfo?.billedAt);
const getTherapistName = (row: PatientListDto): string => row.therapistInfo?.fullName ?? '';

const getSortValue = (row: PatientListDto, key: SortKey): string | number | boolean => {
  switch (key) {
    case 'patient':
      return normalize(`${getPatientLabel(row)} ${row.patientInfo.email}`);
    case 'ssn':
      return normalize(row.patientInfo.ssn);
    case 'onboarding':
      return hasOnboarded(row);
    case 'nextAppointment':
      return row.nextAppointment ?? '';
    case 'status':
      return normalize(getStatusKey(row));
    case 'trainingPlan':
      return hasTrainingPlan(row) ? `1_${row.trainingPlanEndDate ?? ''}` : '0_';
    case 'teleRehaProgram':
      return normalize(getTeleRehaProgramTitle(row));
    case 'documents':
      return getDocumentScore(row);
    case 'therapyBilling':
      return getTherapyBillingsUnitAmount(row);
    case 'billed':
      return isBilled(row);
    case 'therapist':
      return normalize(getTherapistName(row));
  }
};

const PatientsTable: FC<PatientsTableProps> = ({ 
  page,
  rows, 
  showAdditionalInfo, 
  onNavigate, 
  onPatientEdit, 
  onUploadFile, 
  onSetCompletionConversation,
  onCompletePatient,
  onBillingPatient,
  onDeletePatient,
  onCancelPatient,
  onExportFile,
  onTherapistChange,
  toolbarRight,
}: PatientsTableProps): ReactElement => {
  const [openIds, setOpenIds] = useState<Set<string>>(new Set());
  const { userId } = useExtendedAuth();
  
  const store = useMemo(() => createPatientTableStore(userId ?? '', page), [userId, page]);
  
  const filters: FilterState = usePatientTableSelector(store, (s) => s.filters);
  const sort: SortState = usePatientTableSelector(store, (s) => s.sort);
  
  const setPatientQuery: (value: string) => void = usePatientTableSelector(store, (s) => s.setPatientQuery);
  const setFilter = usePatientTableSelector(store, (s) => s.setFilter);
  const toggleSort: (key: SortKey) => void = usePatientTableSelector(store, (s) => s.toggleSort);
  const resetFilters: () => void = usePatientTableSelector(store, (s) => s.resetFilters);
  
  const onSearchPatient: (value: string) => void = useCallback((value: string) => {
    setPatientQuery(value);
  }, [setPatientQuery]);
  
  
  const onSortClick = (key: SortKey): void => {
    toggleSort(key);
  };
  
  const statusOptions: string[] = useMemo((): string[] => {
    const set: Set<string> = new Set<string>();
    for (const row of rows) {
      const status: string = getStatusKey(row);
      if (status.trim().length > 0) {
        set.add(status);
      }
    }
    return Array.from(set).sort((a: string, b: string) => compareString(normalize(a), normalize(b)));
  }, [rows]);
  
  const teleRehaOptions: { id: string, title: string }[] = useMemo((): Array<{ id: string, title: string }> => {
    const map: Map<string, string> = new Map<string, string>();
    for (const row of rows) {
      const id: string = getTeleRehaProgramId(row);
      if (id.length === 0) {
        continue;
      }
      const title: string = getTeleRehaProgramTitle(row);
      if (!map.has(id)) {
        map.set(id, title);
      }
    }
    
    const list: { id: string, title: string }[] = Array.from(map.entries()).map(([id, title]: [string, string]) => ({ id, title }));
    list.sort((a: { id: string, title: string }, b: { id: string, title: string }) => compareString(normalize(a.title), normalize(b.title)));
    return list;
  }, [rows]);
  
  const activeFilterCount: number = useMemo(() => {
    let count: number = 0;
    
    if (filters.onboarding.length > 0) {
      count += 1;
    }
    
    if (filters.status.length > 0) {
      count += 1;
    }
    
    if (filters.trainingPlan.length > 0) {
      count += 1;
    }
    
    if (filters.teleRehaProgramId.length > 0) {
      count += 1;
    }
    
    return count;
  }, [filters.onboarding, filters.status, filters.trainingPlan, filters.teleRehaProgramId]);
  
  let filterIconColor: string = 'var(--bs-secondary-color)';
  if (activeFilterCount > 0) {
    filterIconColor = 'var(--bs-primary)';
  }
  
  const visibleRows: PatientListDto[] = useMemo(() => {
    const query: string = normalize(filters.patientQuery);
    
    const filtered: PatientListDto[] = rows.filter((row: PatientListDto) => {
      if (query.length > 0) {
        const patient: string = normalize(`${getPatientLabel(row)} ${row.patientInfo.email}`);
        if (!patient.includes(query)) {
          return false;
        }
      }
      
      if (filters.onboarding === 'onboarded' && !hasOnboarded(row)) {
        return false;
      }
      
      if (filters.onboarding === 'notOnboarded' && hasOnboarded(row)) {
        return false;
      }
      
      if (filters.status.length > 0 && getStatusKey(row) !== filters.status) {
        return false;
      }
      
      if (filters.trainingPlan === 'hasPlan' && !hasTrainingPlan(row)) {
        return false;
      }
      
      if (filters.trainingPlan === 'noPlan' && hasTrainingPlan(row)) {
        return false;
      }
      
      return !(filters.teleRehaProgramId.length > 0 && getTeleRehaProgramId(row) !== filters.teleRehaProgramId);
    });
    
    if (!sort) {
      return filtered;
    }
    
    return [...filtered].sort((a: PatientListDto, b: PatientListDto) => {
      if (sort.key === 'nextAppointment') {
        const compare: number = compareDate(a.nextAppointment, b.nextAppointment);
        return sort.dir === 'asc' ? compare : -compare;
      }
      
      const aValue: string | number | boolean = getSortValue(a, sort.key);
      const bValue: string | number | boolean = getSortValue(b, sort.key);
      
      let compare: number;
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        compare = compareNumber(aValue, bValue);
      } else if (typeof aValue === 'boolean' && typeof bValue === 'boolean') {
        compare = compareBoolean(aValue, bValue);
      } else {
        compare = compareString(normalize(aValue), normalize(bValue));
      }
      
      return sort.dir === 'asc' ? compare : -compare;
    });
  }, [rows, filters, sort]);

  const isOpen = (id: string): boolean => openIds.has(id);
  const toggle = (id: string): void => {
    setOpenIds((previous: Set<string>) => {
      const next: Set<string> = new Set(previous);
      
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };
  
  const filterPopover: OverlayChildren = (
    <Popover id="patients-filter-popover" style={{ width: 360, borderRadius: 12, overflow: 'hidden' }}>
      
      <div className="d-flex align-items-center justify-content-between px-3 py-2 bg-body-tertiary border-bottom">
        <div className="fw-semibold" style={{ fontSize: 16 }}>{i18next.t('generic.filter')}</div>
        
        <Button 
          size="sm" 
          variant="outline-primary" 
          className="d-inline-flex align-items-center" 
          style={{ height: 30 }} 
          onClick={resetFilters}
        >
          {i18next.t('generic.resetFilter')}
        </Button>
      </div>
      
      <div className="p-3">
        <Form>
          <Form.Group className="mb-3">
            <Form.Label className="text-uppercase text-muted fw-semibold mb-1" style={{ fontSize: 12 }}>
              {i18next.t('patientsTable.onboarding')}
            </Form.Label>
            <Form.Select 
              className="select-xl"
              value={filters.onboarding}
              onChange={(event) => setFilter('onboarding', event.target.value as FilterState['onboarding'])}
            >
              <option value="">{i18next.t('patientsTable.filter.onboarding.all')}</option>
              <option value="onboarded">{i18next.t('patientsTable.filter.onboarding.onboarded')}</option>
              <option value="notOnboarded">{i18next.t('patientsTable.filter.onboarding.notOnboarded')}</option>
            </Form.Select>
          </Form.Group>
          
          <Form.Group className="mb-3">
            <Form.Label className="text-uppercase text-muted fw-semibold mb-1" style={{ fontSize: 12 }}>
              {i18next.t('patientsTable.status.title')}
            </Form.Label>
            <Form.Select
              className="select-xl"
              value={filters.status}
              onChange={(event) => setFilter('status', event.target.value)}>
              <option value="">{i18next.t('patientsTable.filter.status.all')}</option>
              {statusOptions.map((status: string) => (
                <option key={status} value={status}>{i18next.t(`patientsTable.status.${status}`)}</option>
              ))}
            </Form.Select>
          </Form.Group>
          
          <Form.Group className="mb-3">
            <Form.Label className="text-uppercase text-muted fw-semibold mb-1" style={{ fontSize: 12 }}>
              {i18next.t('patientsTable.trainingPlan')}
            </Form.Label>
            <Form.Select
              className="select-xl"
              value={filters.trainingPlan}
              onChange={(event) => setFilter('trainingPlan', event.target.value as FilterState['trainingPlan'])}>
              <option value="">{i18next.t('patientsTable.filter.trainingPlan.all')}</option>
              <option value="hasPlan">{i18next.t('patientsTable.filter.trainingPlan.hasPlan')}</option>
              <option value="noPlan">{i18next.t('patientsTable.filter.trainingPlan.noPlan')}</option>
            </Form.Select>
          </Form.Group>
          
          <Form.Group className="mb-1">
            <Form.Label className="text-uppercase text-muted fw-semibold mb-1" style={{ fontSize: 12 }}>
              {i18next.t('patientsTable.teleRehaProgram')}
            </Form.Label>
            <Form.Select
              className="select-xl"
              value={filters.teleRehaProgramId}
              onChange={(event) => setFilter('teleRehaProgramId', event.target.value)}>
              <option value="">{i18next.t('patientsTable.filter.teleRehaProgram.all')}</option>
              {teleRehaOptions.map((option: { id: string, title: string }) => (
                <option key={option.id} value={option.id}>{option.title}</option>
              ))}
            </Form.Select>
          </Form.Group>
        </Form>
      </div>
    </Popover>
  );
  
  return (
    <>
      <div className="d-flex align-items-start justify-content-between gap-3 mb-3">
        <div className="d-flex align-items-start gap-2 flex-grow-1" style={{ minWidth: 260 }}>
          <SearchBar onSearch={onSearchPatient} maxWidth={400} />
          <OverlayTrigger trigger="click" placement="bottom-start" rootClose overlay={filterPopover}>
            <Button 
              variant="link" 
              className="p-0 d-inline-flex align-items-center justify-content-center"
              aria-label={i18next.t('generic.filter')}
              title={i18next.t('generic.filter')}
              style={{
                width: 33.5,
                height: 33.5,
                color: filterIconColor,
                textDecoration: 'none',
                borderColor: 'var(--bs-border-color)',
                backgroundColor: 'var(--bs-body-bg)'
              }}>
              { activeFilterCount > 0 ? (<Filter size={20} fill="currentColor" />) : (<Filter size={20} />)}
            </Button>
          </OverlayTrigger>
        </div>

        <div className="d-flex align-items-center gap-2">
          {toolbarRight}
        </div>
      </div>
      
      <div className="table-responsive">
        <Table className="align-middle hover" style={{ tableLayout: 'fixed', minWidth: showAdditionalInfo ? 1260 : 1020 }}>
          <colgroup>
            <col style={{ width: 32 }} />
            {showAdditionalInfo ? (
              <>
                <col style={{ width: '15%' }} />
                <col style={{ width: '6%' }} />
                <col style={{ width: '6%' }} />
                <col style={{ width: '13%' }} />
                <col style={{ width: '8%' }} />
                <col style={{ width: '6%' }} />
                <col style={{ width: '11%' }} />
                <col style={{ width: '14%' }} />
                <col style={{ width: '6%' }} />
                <col style={{ width: '5%' }} />
                <col style={{ width: '10%' }} />
              </>
            ) : (
              <>
                <col style={{ width: '20%' }} />
                <col style={{ width: '9%' }} />
                <col style={{ width: '9%' }} />
                <col style={{ width: '14%' }} />
                <col style={{ width: '10%' }} />
                <col style={{ width: '9%' }} />
                <col style={{ width: '15%' }} />
                <col style={{ width: '14%' }} />
              </>
            )}
            <col style={{ width: 32 }} />
          </colgroup>
          <thead>
            <tr>
              <th style={{ width: 32 }} />
              
              <SortableTableHeader label={i18next.t('patientsTable.patient')} sortKey="patient" sort={sort} onSort={onSortClick} />
              <SortableTableHeader label={i18next.t('patientsTable.ssn')} sortKey='ssn' sort={sort} onSort={onSortClick} />
              <SortableTableHeader label={i18next.t('patientsTable.onboarding')} sortKey="onboarding" sort={sort} onSort={onSortClick} />
              <SortableTableHeader label={i18next.t('patientsTable.nextAppointment')} sortKey="nextAppointment" sort={sort} onSort={onSortClick} />
              <SortableTableHeader label={i18next.t('patientsTable.status.title')} sortKey="status" sort={sort} onSort={onSortClick} />
              <SortableTableHeader label={i18next.t('patientsTable.trainingPlan')} sortKey="trainingPlan" sort={sort} onSort={onSortClick} />
              <SortableTableHeader label={i18next.t('patientsTable.teleRehaProgram')} sortKey="teleRehaProgram" sort={sort} onSort={onSortClick} />
              <SortableTableHeader label={i18next.t('patientsTable.documents')} sortKey="documents" sort={sort} onSort={onSortClick} />

              {showAdditionalInfo && (
                <>
                  <SortableTableHeader label={i18next.t('patientsTable.therapyBilling')} sortKey="therapyBilling" sort={sort} onSort={onSortClick} />
                  <SortableTableHeader label={i18next.t('patientsTable.billed')} sortKey="billed" sort={sort} onSort={onSortClick} />
                  <SortableTableHeader label={i18next.t('patientsTable.therapist')} sortKey="therapist" sort={sort} onSort={onSortClick} />
                </>
              )}
              
              <th style={{ width: 32 }} />
            </tr>
          </thead>
          
          <tbody>
            {visibleRows.map((row: PatientListDto) => {
              const isStarted: boolean = row.patientInfo.status === PatientTherapyStatus.STARTED;

              return (
                <PatientRow
                  key={row.therapyId}
                  row={row}
                  showAdditionalInfo={showAdditionalInfo}
                  isOpen={isOpen(row.therapyId)}
                  onToggle={() => toggle(row.therapyId)}
                  onNavigate={onNavigate ? (): void => onNavigate(row.therapyId) : undefined}
                  onEditPatient={(row: PatientListDto) => onPatientEdit?.(row)}
                  onUploadApplication={(row: PatientListDto) => onUploadFile?.({ patient: row, isCompletion: false })}
                  onUploadFinal={(row: PatientListDto) => onUploadFile?.({ patient: row, isCompletion: true })}
                  onSetCompletionConversation={
                    isStarted && !row.hasCompletionConversation ? onSetCompletionConversation : undefined
                  }
                  onCompletePatient={onCompletePatient}
                  onBillPatient={
                    onBillingPatient
                      ? (row: PatientListDto): void => onBillingPatient?.({ patientId: row.id, billingInfo: row.therapyBillingInfo })
                      : undefined
                  }
                  onDeletePatient={onDeletePatient}
                  onCancelPatient={onCancelPatient}
                  onTherapistChange={onTherapistChange}
                  onExportFile={onExportFile}
                />
              );
            })}
          </tbody>
        </Table>
      </div>
    </>
  );
};

export default memo(PatientsTable);
